import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { marked } from "marked";
import { BookOpen, Download, FileSpreadsheet, FileText, FileType2, Loader2, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, download, errMsg } from "@/lib/api";
import { loadDoc, guideTokens, downloadPdf, downloadDocx } from "@/lib/guideExport";

const DOC = { title: "EUDR First 2 weeks plan", file: "EP-Group-EUDR-First-2-weeks-plan" };
const SAP_DOC = { title: "SAP updates for SAP IT Team", file: "EP-Group-EUDR-SAP-updates-for-SAP-IT-Team" };
const cell = (s) => String(s ?? "").replace(/\|/g, "/");

async function sapPdf() {
  const { data } = await api.get("/plan/sap-updates");
  const md = [`# EP Group EUDR — SAP updates for ${data.owner}`, "",
    `Real counts as at ${data.as_at}. Owner of every item: **${data.owner}**. Suggested SAP fields are marked "suggested — confirm with SAP team". Once an item is maintained in SAP, the daily or weekly extract carries it into the system and manual entry stops. The record IDs for each item are in the Excel workbook (one sheet per item).`, "",
    "| # | Data item | Suggested SAP field / table | Records to update | Owner | Upload / script | Upload change needed | Status | Target date |", "|---|---|---|---|---|---|---|---|---|",
    ...data.items.map((x, i) => `| ${i + 1} | ${cell(x.item)} | ${cell(x.sap)} | ${cell(x.records)} | ${cell(x.owner)} | ${cell(x.upload)} | ${cell(x.change_needed)} | ${x.status} | ${x.target} |`), ""].join("\n");
  await downloadPdf(guideTokens(md), SAP_DOC);
}

export default function FirstTwoWeeksPlan() {
  const { can } = useAuth();
  if (!can.admin) return <div className="max-w-xl rounded-xl border border-slate-200 bg-white p-6" data-testid="plan-admins-only"><h1 className="text-lg font-semibold text-slate-900">Admins only</h1><p className="mt-1 text-sm text-slate-500">The First 2 weeks plan is available to Admin users.</p></div>;
  return <PlanPage />;
}

function PlanPage() {
  const [tokens, setTokens] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { loadDoc("/first-2-weeks-plan.md").then((md) => setTokens(guideTokens(md))); }, []);
  const run = (kind, fn) => async () => {
    setBusy(kind);
    try { await fn(); toast.success("Download ready"); } catch (e) { toast.error(`Could not build the ${kind}: ${errMsg(e)}`); } finally { setBusy(null); }
  };
  const icon = (k, I) => (busy === k ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <I className="mr-2 h-4 w-4" />);
  return (
    <div className="space-y-5" data-testid="plan-page">
      <PageHeader title="First 2 weeks plan" subtitle="Day-by-day tasks for every owner, with real record counts, the SAP updates that automate the data links, indicators and risks.">
        <Button data-testid="plan-download-pdf" disabled={!tokens || !!busy} onClick={run("PDF", () => downloadPdf(tokens, DOC))}>{icon("PDF", FileText)}Download PDF</Button>
        <Button variant="outline" data-testid="plan-download-docx" disabled={!tokens || !!busy} onClick={run("Word document", () => downloadDocx(tokens, DOC))}>{icon("Word document", FileType2)}Download Word (.docx)</Button>
        <Button variant="outline" data-testid="plan-download-tracker" disabled={!!busy}
          onClick={run("Excel tracker", () => download("/plan/tracker.xlsx", `EP Group EUDR first 2 weeks tracker ${new Date().toISOString().slice(0, 10)}.xlsx`))}>
          {icon("Excel tracker", FileSpreadsheet)}Download Excel tracker</Button>
        <Button asChild variant="ghost" data-testid="plan-open-guide"><Link to="/guide"><BookOpen className="mr-2 h-4 w-4" />Process guide</Link></Button>
      </PageHeader>
      <section className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4" data-testid="plan-sap-updates">
        <Wrench className="h-5 w-5 text-[#D52B3B]" />
        <div className="mr-auto">
          <p className="text-sm font-semibold text-slate-900">SAP updates for SAP IT Team</p>
          <p className="text-xs text-slate-500">Standalone list of SAP field updates with live counts; the Excel version adds a sheet per item with the record IDs to update.</p>
        </div>
        <Button variant="outline" data-testid="plan-sap-download-xlsx" disabled={!!busy}
          onClick={run("SAP updates workbook", () => download("/plan/sap-updates.xlsx", `EP Group EUDR SAP updates for SAP IT Team ${new Date().toISOString().slice(0, 10)}.xlsx`))}>
          {icon("SAP updates workbook", FileSpreadsheet)}SAP updates (Excel)</Button>
        <Button variant="outline" data-testid="plan-sap-download-pdf" disabled={!!busy} onClick={run("SAP updates PDF", sapPdf)}>{icon("SAP updates PDF", FileText)}SAP updates (PDF)</Button>
      </section>
      {!tokens ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : (
        <article className="guide-prose max-w-5xl rounded-2xl border border-slate-200 bg-white px-8 py-8 shadow-sm" data-testid="plan-content">
          {tokens.map((t, i) => <div key={i} dangerouslySetInnerHTML={{ __html: marked.parser([t]) }} />)}
        </article>
      )}
      <p className="flex items-center gap-1.5 text-xs text-slate-400"><Download className="h-3.5 w-3.5" />PDF and Word are built in your browser from the text shown here. The Excel tracker is built live from the current data.</p>
    </div>
  );
}
